import axios from "axios";
import { BASE_URL } from "../common/baseUrl";
import { getClientSource } from "../common/Functions";
import { APIS } from "../constants/apiConstant";

export const BOARD_ATTACHMENT_ACCEPT =
  ".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.webp,.gif";

export const BOARD_ATTACHMENT_MAX_BYTES = 10 * 1024 * 1024;

const ALLOWED_MIME_TYPES = new Set([
  "application/pdf",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/msword",
  "image/jpeg",
  "image/png",
  "image/webp",
  "image/gif",
]);

export const normalizeBoardAttachment = (item = {}) => ({
  attachmentId: item.attachmentId || item.attachment_id,
  cardId: item.cardId || item.card_id,
  fileName: item.fileName || item.file_name || "attachment",
  fileType: item.fileType || item.file_type || "",
  fileSizeKb: Number(item.fileSizeKb ?? item.file_size_kb ?? 0) || 0,
  uploadedAt: item.uploadedAt || item.uploaded_at || null,
  thumbnailUrl: item.thumbnailUrl || item.thumbnail_url || null,
  uploader: item.uploader || null,
});

export const isImageAttachment = (fileType) =>
  String(fileType || "").toLowerCase().startsWith("image/");

export const isPdfAttachment = (fileType) =>
  String(fileType || "").toLowerCase() === "application/pdf";

export const canPreviewBoardAttachment = (fileType) =>
  isImageAttachment(fileType) || isPdfAttachment(fileType);

export function buildLargePreviewUrl(thumbnailUrl) {
  if (!thumbnailUrl) return null;
  return String(thumbnailUrl)
    .replace(/w_80,h_80,c_fill,g_auto/, "w_1600,q_auto")
    .replace(/w_80,h_80,c_fill/, "w_1600,q_auto");
}

export function validateBoardAttachmentFile(file) {
  if (!file) {
    return { ok: false, message: "No file selected." };
  }
  if (file.size > BOARD_ATTACHMENT_MAX_BYTES) {
    return { ok: false, message: "File is too large (max 10MB)." };
  }
  const mime = String(file.type || "").toLowerCase();
  if (mime && !ALLOWED_MIME_TYPES.has(mime)) {
    return { ok: false, message: "Only PDF, Office documents, or image files are allowed." };
  }
  return { ok: true };
}

export function parseActivityMetadata(metadata) {
  if (!metadata) return {};
  if (typeof metadata === "object") return metadata;
  try {
    return JSON.parse(metadata);
  } catch {
    return {};
  }
}

export function attachmentFromActivityMeta(cardId, meta = {}) {
  if (!meta.attachmentId) return null;
  return normalizeBoardAttachment({
    attachmentId: meta.attachmentId,
    cardId,
    fileName: meta.fileName,
    fileType: meta.fileType,
  });
}

/** Loads attachment bytes through the API (no Cloudinary URL in the browser). */
export async function fetchBoardAttachmentBlob(cardId, attachmentId) {
  const accessToken = localStorage.getItem("accessToken");
  const url = APIS.PROJECT_BOARD_CARD_ATTACHMENT_VIEW.replace(":cardId", cardId).replace(
    ":attachmentId",
    attachmentId
  );

  const res = await axios.get(`${BASE_URL}${url}`, {
    responseType: "arraybuffer",
    headers: {
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      company_source: getClientSource(),
    },
    validateStatus: () => true,
  });

  if (res.status === 401) {
    window.location.href = "/login";
    throw new Error("Unauthorized");
  }

  if (res.status !== 200) {
    throw new Error(`Could not load the attachment (${res.status}).`);
  }

  const contentType = res.headers["content-type"] || "application/octet-stream";
  return new Blob([res.data], { type: contentType });
}

export function downloadBoardAttachmentBlob(blob, fileName = "attachment") {
  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = fileName;
  anchor.rel = "noopener";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
}
